<template>
    <page :busy="busy" :tabs="tabs" class="unlayer-template-editor">
        <div class="w-100 h-100 pt-2">
            <div class="editor-wrapper">
                <unlayer-templates-sidebar
                    :system-templates="systemTemplates"
                    @select-template="selectTemplate"
                    :template="template"
                />
                <div class="w-100 h-100 d-flex flex-column">
                    <div class="d-flex justify-content-between align-items-center pl-4 pb-2 pr-4">
                        <div class="d-flex align-items-baseline">
                            <div class="d-flex flex-column align-items-start">
                                <h4 v-if="!showEdit" class="mb-0">{{ templateId ? template.name : 'Templates' }}</h4>
                                <small class="text-muted"> Tip: Use @ to search merge tags </small>
                                <responsive-button
                                    v-if="showTextEditor"
                                    :icon="['fas', 'arrow-left']"
                                    variant="outline-primary"
                                    @click="toggleTextEditor"
                                    class="mt-2"
                                    >Go To HTML</responsive-button
                                >
                            </div>
                        </div>
                        <div v-if="isNonEditable" class="text-muted">
                            This template cannot be edited.
                        </div>
                        <div class="d-flex align-items-center">
                            <responsive-button
                                v-if="isImpersonated && template.require_validation"
                                :icon="['fas', 'check']"
                                @click="validateTemplate"
                                variant="outline-primary"
                                :with-space="false"
                                class="mr-2"
                                >Validate</responsive-button
                            >
                            <responsive-button
                                :icon="['fas', 'plus']"
                                @click="$bvModal.show('create-template-modal')"
                                variant="outline-primary"
                                :with-space="false"
                            ></responsive-button>

                            <!-- <responsive-button
                                class="ml-2"
                                :icon="['fas', 'wand-magic-sparkles']"
                                @click="$bvModal.show('ai-scan-template-modal')"
                                variant="outline-primary"
                                :with-space="false"
                            ></responsive-button> -->

                            <unlayer-templates-ai-scan-modal
                                :modal-id="'ai-scan-template-modal'"
                                :template-id="templateId"
                                @submit="fetchData"
                            />

                            <responsive-button
                                v-if="!isNonEditable"
                                class="ml-2"
                                @click="templateId ? onSave() : $bvModal.show('create-template-modal')"
                                variant="outline-primary"
                                :busy="internalLoading"
                                >{{ internalLoading ? 'Saving...' : templateId ? 'Save Template' : 'Create Template' }}</responsive-button
                            >
                            <responsive-button
                                v-if="canDeleteTemplate"
                                :icon="['fas', 'trash']"
                                class="ml-2"
                                @click="$bvModal.show('delete-template-modal')"
                                variant="outline-primary"
                                :with-space="false"
                            ></responsive-button>
                            <b-button-group v-if="templateId && !isNonEditable" class="ml-2">
                                <more-filters-dropdown class="unlayer-dropdown" variant="outline-primary">
                                    <responsive-button
                                        :icon="['fas', 'cog']"
                                        class="mb-2"
                                        @click="$bvModal.show('edit-template-modal')"
                                        variant="outline-primary"
                                        >Template Settings</responsive-button
                                    >
                                    <!-- <responsive-button
                                        :icon="['fas', 'cogs']"
                                        class="mb-2"
                                        @click="$bvModal.show('unlayer-template-global-settings-modal')"
                                        variant="outline-primary"
                                        >Global Settings</responsive-button
                                    > -->
                                    <responsive-button
                                        :icon="['far', 'file-pdf']"
                                        class="mb-2"
                                        @click="pdf"
                                        variant="outline-primary"
                                        >View PDF</responsive-button
                                    >
                                    <responsive-button
                                        :icon="['fas', 'envelope']"
                                        @click="$bvModal.show('send-test-email-modal')"
                                        variant="outline-primary"
                                        >Send Test Email</responsive-button
                                    >
                                    <responsive-button
                                        v-if="showCloneButton"
                                        :icon="['fas', 'clone']"
                                        @click="cloneTemplate"
                                        variant="outline-primary"
                                        >Clone</responsive-button
                                    >
                                    <responsive-button
                                        :icon="['fas', 'copy']"
                                        @click="extractText"
                                        variant="outline-primary"
                                        >Extract Text</responsive-button
                                    >
                                    <responsive-button
                                        v-if="template.is_default"
                                        :icon="['fas', 'undo']"
                                        @click="$bvModal.show('restore-template-modal')"
                                        variant="outline-primary"
                                        >Restore</responsive-button
                                    >
                                </more-filters-dropdown>
                            </b-button-group>
                        </div>
                    </div>
                    <div class="w-100 h-100 editor-container" v-show="!showPreview && !showTextEditor">
                        <div class="text-icon-container cursor-pointer" @click="toggleTextEditor" v-if="!isNonEditable">
                            <font-awesome-icon :icon="['fas', 'file-lines']" />
                        </div>
                        <email-editor
                            v-show="templateId"
                            editor-id="unlayer-editor"
                            ref="emailEditor"
                            :style="{ height: '100%', width: '100%' }"
                            :options="unlayerOptions"
                            :key="templateId"
                            @ready="loadDesign"
                            :project-id="266990"
                        />
                    </div>
                    <div class="pl-4 pr-2 w-100 h-100" v-show="!showPreview && showTextEditor">
                        <form-search-field
                            ref="search_field"
                            class="w-100 mb-0 tag-search-field"
                            :options="smsTagsOptions"
                            @input="onSearchTag"
                            :value="tagSearch"
                            @select="(text, value) => selectTag(text, value)"
                            :rightButton="rightButtonConfig"
                            :rightButton2="rightButtonConfig2"
                            placeholder="Search Tag"
                            @right-button-click="onRightButtonClick"
                            @right-button2-click="insertTextTag"
                            :isGrouped="true"
                        />
                        <form-textarea
                            ref="plain_text"
                            floating-label="Plain text"
                            v-model="template.plain_text"
                            :rows="25"
                        />
                    </div>
                    <div class="pl-4 pr-2 w-100 h-100" v-show="showPreview && !showTextEditor">
                        <responsive-button
                            :icon="['fas', 'arrow-left']"
                            variant="outline-primary"
                            @click="showPreview = false"
                            class="mb-2"
                            >Go Back</responsive-button
                        >
                        <iframe :src="pdfUrl" width="100%" height="100%"></iframe>
                    </div>
                </div>
            </div>

            <validation-observer ref="send_test_email_form" v-slot="{ handleSubmit, meta }">
                <modal
                    @ok="handleSubmit(sendTestEmail)"
                    :ok-disabled="!meta.valid"
                    id="send-test-email-modal"
                    title="Send Test Email"
                    @shown="setEmailData"
                >
                    <validation-provider
                        validate-on-input
                        v-slot="{ errors, field }"
                        name="email"
                        rules="required|email"
                    >
                        <form-input v-bind="field" :error="errors[0]" floatingLabel="Email" />
                    </validation-provider>
                </modal>
            </validation-observer>
            <unlayer-templates-create-modal
                :modal-id="'edit-template-modal'"
                :predefined-templates="predefinedTemplates"
                :initial-values="template"
                @submit="onSave"
            />
            <unlayer-templates-create-modal
                :modal-id="'create-template-modal'"
                :predefined-templates="predefinedTemplates"
                @submit="onCreate"
            />
            <modal id="delete-template-modal" title="Delete Template" @ok="onDelete">
                <p>
                    Are you sure you want to delete <b>{{ template.name }}</b> template?
                </p>
            </modal>
            <modal id="restore-template-modal" title="Restore Template" @ok="restoreTemplate">
                <p>
                    Are you sure you want to restore <b>{{ template.name }}</b> template?
                </p>
            </modal>
        </div>
        <unlayer-template-global-settings-modal
            :modal-id="'unlayer-template-global-settings-modal'"
            @save="saveGlobalSettings"
            :initial-settings="globalSettings"
        />
    </page>
</template>

<script>
import { EmailEditor } from 'vue-email-editor';
import { mapGetters } from 'vuex';
import { toRaw } from 'vue';
import api from '../../../api';
import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormSearchField from '../../generic/form-search-field/form-search-field.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import formSelect from '../../generic/form-select/form-select.vue';
import Modal from '../../generic/modal/modal.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import Page from '../../generic/page/page.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import { formatResponseError } from '../../../utils/api';
import { MOST_COMMON_TAGS, TEMPLATE_GROUPS, TEMPLATE_TYPES, TEMPLATE_IDENTIFIERS } from './constants';
import { RIGHT_BUTTON_CONFIG, RIGHT_BUTTON_2_CONFIG } from '../../trigger/trigger-edit/helpers/constants';
import { UNLAYER_TEMPLATE_BY_ID_ENDPOINT, UNLAYER_TEMPLATE_SAVED_BLOCK_ENDPOINT } from '../../../endpoints';
import UnlayerTemplateGlobalSettingsModal from './unlayer-template-global-settings-modal.vue';
import UnlayerTemplatesCreateModal from './unlayer-templates-create-modal.vue';
import UnlayerTemplatesAiScanModal from './unlayer-templates-ai-scan-modal.vue';
import UnlayerTemplatesSidebar from './unlayer-templates-sidebar.vue';
import upperCase from 'lodash/upperCase';
import debounce from 'lodash/debounce';

export default {
    name: 'TemplateEditor',
    components: {
        EmailEditor,
        FormCheckbox,
        FormInput,
        FormSearchField,
        FormTextarea,
        Modal,
        MoreFiltersDropdown,
        Page,
        ResponsiveButton,
        UnlayerTemplateGlobalSettingsModal,
        UnlayerTemplatesCreateModal,
        UnlayerTemplatesSidebar,
        UnlayerTemplatesAiScanModal,
        formSelect,
    },
    data() {
        return {
            busy: false,
            collapse: {},
            currentTab: 'html',
            internalLoading: false,
            mergeTagsGroupped: {},
            pdfUrl: '',
            predefinedTemplates: [],
            savedBlocks: [],
            selectedTag: '',
            showEdit: false,
            showPreview: false,
            showTextEditor: false,
            sidebar: {
                system: true,
            },
            smsTagsOptions: [],
            systemTemplates: {},
            tagSearch: '',
            template: {
                design: {},
                html: '',
                is_default_rental_agreement: false,
                name: 'New Template',
                plain_text: '',
                require_validation: false,
                template_group: TEMPLATE_GROUPS.CUSTOM,
                template_type: TEMPLATE_TYPES.USER.value,
                use_as_pdf: false,
            },
            textVariables: [],
        };
    },
    computed: {
        ...mapGetters('header', ['userEmail', 'userIsAdmin', 'userID', 'userFullName', 'isImpersonated']),
        unlayerOptions() {
            const options = {
                version: 'latest',
                devices: ['desktop'],
                displayMode: 'web',
                mergeTagsConfig: { autocompleteTriggerChar: '@', sort: false },
                designMode: this.userIsAdmin ? 'edit' : 'live',
                projectId: 266990,
                fonts: {
                    showDefaultFonts: true,
                },
                editor: {
                    autoSelectOnDrop: true,
                },
                features: {
                    textEditor: {
                        tables: true,
                    },
                    ai: {
                        smartText: false,
                    },
                },
                tools: {
                    form: {
                        enabled: false,
                    },
                    carousel: {
                        enabled: false,
                    },
                    columns: {
                        position: 1,
                    },
                    timer: {
                        enabled: false,
                    },
                    menu: {
                        enabled: false,
                    },
                    rchargestable: {
                        position: 1,
                    },
                    guest_folio: {
                        position: 2,
                    },
                    text: {
                        properties: {
                            text: {
                                value: ' ',
                            },
                        },
                        position: 4,
                    },
                    image: {
                        position: 5,
                    },
                    button: {
                        position: 6,
                    },
                    divider: {
                        position: 7,
                    },
                    video: {
                        position: 8,
                    },
                    social: {
                        position: 9,
                    },
                },
                user: {
                    id: this.userID,
                    email: this.userEmail,
                    name: this.userFullName,
                },
                appearance: {
                    loader: {
                        html: '<div class="spinner-border text-primary" role="status"></div>',
                        css:
                            '.spinner-border { display: inline-block; width: 5rem; height: 5rem; vertical-align: text-bottom; border: 0.5em solid rgb(12, 72, 161); border-right-color: transparent; border-radius: 50%; animation: spinner-border 1s linear infinite; } @keyframes spinner-border { to { transform: rotate(360deg); } }',
                    },
                },
                customJS: [
                    `${window.location.origin}/static/unlayer/rchargestable.js`,
                    `${window.location.origin}/static/unlayer/guest_folio.js`,
                    `${window.location.origin}/static/unlayer/quote_chargestable.js`,
                ],
            };
            if (this.isNonEditable) {
                options.editor.minRows = 1;
                options.editor.maxRows = 1;
            }
            return options;
        },
        templateId() {
            return parseInt(this.$route.params.id);
        },
        isNonEditable() {
            return [TEMPLATE_IDENTIFIERS.NEW_GUEST_BOOKING].includes(this.template.identifier);
        },
        tabs() {
            return [
                {
                    title: 'Communications',
                    route: '/app/saved-replies/0',
                    active: true,
                },
            ];
        },
        templateTypeLabel() {
            return this.templateTypes[this.template.template_group].find(
                (type) => type.value === this.template.template_type
            )?.text;
        },
        rightButtonConfig() {
            return RIGHT_BUTTON_CONFIG;
        },
        rightButtonConfig2() {
            return RIGHT_BUTTON_2_CONFIG;
        },
        showCloneButton() {
            return [TEMPLATE_TYPES.USER.value, TEMPLATE_TYPES.RENTAL_AGREEMENT.value].includes(
                this.template.template_type
            );
        },
        globalSettings() {
            const bodyValues = this.template.design?.body?.values || {};
            return {
                ...bodyValues,
                fontFamily: bodyValues?.fontFamily?.value || 'arial,helvetica,sans-serif',
            };
        },
        canDeleteTemplate() {
            if (this.template.template_type === TEMPLATE_TYPES.RENTAL_AGREEMENT.value) {
                return !this.template.is_default_rental_agreement;
            }
            return !this.template.is_default;
        },
    },
    mounted() {
        this.fetchData();
        this.fetchPredefinedTemplates();
    },
    beforeDestroy() {
        if (this.debounceDesignUpdate.cancel) {
            this.debounceDesignUpdate.cancel();
        }
    },
    methods: {
        async cloneTemplate() {
            try {
                this.busy = true;
                const { data } = await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId) + 'clone/');
                this.$router.push({
                    path: `/app/saved-replies/${data.id}`,
                });
                this.template = data;
                this.fetchTemplatesList();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not clone template' });
            } finally {
                this.busy = false;
            }
        },
        createNewTemplate() {
            this.$bvModal.show('create-template-modal');
        },
        toggleTextEditor() {
            this.showTextEditor = !this.showTextEditor;
            this.showPreview = false;
            if (this.showTextEditor && !this.template.plain_text) {
                this.$refs.emailEditor.editor.exportPlainText((data) => {
                    this.template.plain_text = data.text;
                });
            }
        },
        toggleEditTitle() {
            this.showEdit = !this.showEdit;
            if (!this.showEdit) {
                this.onSave({});
            }
        },
        generateText() {
            this.$refs.emailEditor.editor.exportPlainText((data) => {
                this.template.plain_text = data.text;
            });
        },
        async fetchData() {
            this.busy = true;
            try {
                await Promise.all([
                    this.fetchTemplate(),
                    this.fetchSavedBlocks(),
                    this.fetchTemplatesList(),
                    this.fetchMergeTags(),
                ]);
                this.loadDesign();
            } finally {
                this.busy = false;
            }
        },
        async fetchSavedBlocks() {
            try {
                const { data } = await api.get(UNLAYER_TEMPLATE_SAVED_BLOCK_ENDPOINT());
                this.savedBlocks = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch saved blocks' });
            }
        },
        async saveBlock(data) {
            try {
                await api.post(UNLAYER_TEMPLATE_SAVED_BLOCK_ENDPOINT(), { data });
                this.successToast({ text: 'Block saved successfully' });
                await this.fetchSavedBlocks();
                this.loadBlocks();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save block' });
            }
        },
        async deleteBlock(blockId) {
            try {
                await api.delete(UNLAYER_TEMPLATE_SAVED_BLOCK_ENDPOINT(blockId));
                this.successToast({ text: 'Block deleted successfully' });
                await this.fetchSavedBlocks();
                this.loadBlocks();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not delete block' });
            }
        },
        async updateBlock(blockId, data) {
            try {
                await api.put(UNLAYER_TEMPLATE_SAVED_BLOCK_ENDPOINT(blockId), data);
                this.successToast({ text: 'Block updated successfully' });
                await this.fetchSavedBlocks();
                this.loadBlocks();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not update block' });
            }
        },
        async fetchTemplatesList() {
            try {
                const { data } = await api.get(UNLAYER_TEMPLATE_BY_ID_ENDPOINT());
                this.systemTemplates = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch system templates' });
            }
        },
        async fetchMergeTags() {
            try {
                const { data } = await api.get(UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'merge-tags/');

                const mergeTagsConfig = {
                    'MOST COMMON': {
                        name: 'MOST COMMON',
                        mergeTags: data.html_variables
                            .filter((item) => MOST_COMMON_TAGS.includes(item.tag))
                            .map((item) => ({
                                name: upperCase(item.description),
                                value: item.tag,
                                sample: upperCase(item.description),
                            })),
                    },
                };

                const groupedMergeTags = data.html_variables.reduce((groups, tag) => {
                    const group = upperCase(tag.group);
                    if (!groups[group]) {
                        groups[group] = {};
                    }
                    groups[group][tag.tag] = {
                        name: upperCase(tag.description),
                        value: tag.tag,
                        sample: upperCase(tag.description),
                    };
                    return groups;
                }, {});

                const allMergeTags = Object.keys(groupedMergeTags)
                    .sort()
                    .reduce((config, groupName) => {
                        config[groupName] = {
                            name: upperCase(groupName),
                            mergeTags: groupedMergeTags[groupName],
                        };
                        return config;
                    }, {});

                this.textVariables = data.text_variables;
                this.mergeTagsGroupped = { ...mergeTagsConfig, ...allMergeTags };
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch merge tags' });
            }
        },
        async fetchTemplate() {
            try {
                if (this.templateId) {
                    const { data } = await api.get(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId));
                    this.template = data;
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch template' });
            }
        },
        async fetchPredefinedTemplates() {
            try {
                const { data } = await api.get(UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'predefined-templates/');
                this.predefinedTemplates = data.map((item) => ({
                    ...item,
                    value: item.id,
                    text: item.title,
                }));
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch predefined templates' });
            }
        },
        async restoreTemplate() {
            try {
                this.busy = true;
                const { data } = await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId) + 'restore-template/');
                this.template = data;
                this.successToast({ text: 'Template restored successfully' });
                await this.fetchTemplatesList();
                this.loadDesign();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not restore template' });
            } finally {
                this.busy = false;
            }
        },
        async selectTemplate(templateData) {
            this.$router.push({
                path: `/app/saved-replies/${templateData.id}`,
            });
            this.template = templateData;
            this.$nextTick(() => {
                this.loadDesign();
            });
        },
        async setAsDefaultRentalAgreement() {
            try {
                this.busy = true;
                await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId) + 'set-default-rental-agreement/');
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not set as default rental agreement' });
            } finally {
                this.busy = false;
            }
        },
        loadBlocks() {
            const blocks = [];
            const savedBlocks = this.savedBlocks.map((item) => ({
                ...toRaw(item.data),
                id: item.id,
            }));
            blocks.push(...savedBlocks);

            const editor = this.$refs.emailEditor.editor;

            editor.registerProvider('blocks', (params, done) => {
                done(blocks);
            });

            editor.reloadProvider('blocks');

            editor.registerCallback('block:added', async (newBlock, done) => {
                done(newBlock);
                await this.saveBlock(newBlock);
            });

            editor.registerCallback('block:removed', async (existingBlock, done) => {
                done(existingBlock);
                await this.deleteBlock(existingBlock.id);
            });

            editor.registerCallback('block:update', async (existingBlock, done) => {
                done(existingBlock);
                await this.updateBlock(existingBlock.id, existingBlock);
            });
        },
        loadDesign() {
            if (!this.$refs.emailEditor?.editor) return;

            const editor = this.$refs.emailEditor.editor;

            if (this.templateId) {
                this.$refs.emailEditor.loadDesign(toRaw(this.template.design));
                editor.setMergeTags(toRaw(this.mergeTagsGroupped));
                this.loadBlocks();
            } else {
                const templates = Object.values(this.systemTemplates).flatMap((groupObj) =>
                    Object.values(groupObj).flat()
                );

                const defaultTemplate = templates.sort((a, b) => {
                    return (
                        new Date(b.modified || '1970-01-01T00:00:00') - new Date(a.modified || '1970-01-01T00:00:00') ||
                        (a.name || '').localeCompare(b.name || '')
                    );
                })[0];

                if (defaultTemplate) {
                    this.$router.push({
                        path: `/app/saved-replies/${defaultTemplate.id}/`,
                    });
                    this.template = defaultTemplate;
                    this.$refs.emailEditor.loadDesign(toRaw(defaultTemplate.design));
                    editor.setMergeTags(toRaw(this.mergeTagsGroupped));
                    this.loadBlocks();
                }
            }

            editor.addEventListener('design:updated', this.debounceDesignUpdate);
        },
        debounceDesignUpdate: debounce(async function() {
            this.internalLoading = true;
            await this.save({ showBusy: false, refetch: false });
            this.internalLoading = false;
        }, 1000),
        setTemplateData(field) {
            if (field === 'create_form') {
                this.clearTemplateData(field);
            } else {
                this.$refs[field].setValues({
                    name: this.template.name,
                    template_type: this.template.template_type,
                    template_group: this.template.template_group,
                });
            }
        },
        clearTemplateData(field) {
            this.$refs[field].setValues({
                name: '',
                template_type: 'CUSTOM',
                template_group: 'CUSTOM',
                template_id: '',
            });
        },
        pdf() {
            this.busy = true;
            this.$refs.emailEditor.saveDesign(async (design) => {
                try {
                    const { data } = await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'pdf/', {
                        design: design,
                        displayMode: 'email',
                    });
                    this.pdfUrl = data.url;
                    this.showPreview = true;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not generate PDF' });
                } finally {
                    this.busy = false;
                }
            });
        },
        onSave(values) {
            this.save({
                callback: () => {
                    this.successToast({ text: 'Template saved successfully' });
                    this.$bvModal.hide('edit-template-modal');
                },
                values,
            });
        },
        insertAtCursor(ref, text) {
            const textarea = ref.$el.querySelector('textarea');
            if (!textarea) return null;

            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const value = textarea.value;
            const newValue = value.substring(0, start) + text + value.substring(end);

            textarea.focus();
            textarea.value = newValue;
            textarea.setSelectionRange(start + text.length, start + text.length);

            return newValue;
        },
        insertTextTag() {
            if (!this.selectedTag) return;

            const text = this.insertAtCursor(this.$refs.plain_text, this.selectedTag);
            if (text) {
                this.template.plain_text = text;
            } else {
                this.template.plain_text = this.template.plain_text
                    ? this.template.plain_text + this.selectedTag
                    : this.selectedTag;
            }
        },
        selectTag(text, value) {
            this.tagSearch = text;
            this.selectedTag = value;
        },
        onRightButtonClick() {
            this.tagSearch = '   ';
            this.selectedTag = this.tagSearch.trim() ? this.selectedTag : '';

            this.smsTagsOptions = this.textVariables.map((item) => ({
                text: `${item.tag}: ${item.description}`,
                value: item.tag,
                group: item.group.toLowerCase(),
            }));
        },
        onSearchTag(value) {
            if (!value.trim()) {
                this.smsTagsOptions = [];
                return;
            }

            const options = this.textVariables.map((item) => ({
                text: `${item.tag}: ${item.description}`,
                value: item.tag,
                group: item.group.toLowerCase(),
            }));

            const filteredOptions = options.filter(
                (item) => item.text.toLowerCase().indexOf(value.trim().toLowerCase()) > 0
            );

            if (options.some((item) => item.text.toLowerCase() !== value.toLowerCase())) {
                this.selectedTag = '';
            }

            this.smsTagsOptions = filteredOptions;
        },
        async onCreate(values) {
            try {
                this.busy = true;
                const selectedTemplate = this.predefinedTemplates.find(
                    (template) => template.id === values.template_id
                );

                const payload = {
                    ...values,
                    design: selectedTemplate.design,
                };

                if (values.is_rental_agreement) {
                    payload.template_type = TEMPLATE_TYPES.RENTAL_AGREEMENT.value;
                    payload.template_group = TEMPLATE_GROUPS.GUEST;
                }

                this.$refs.emailEditor.loadDesign(toRaw(payload.design));
                this.$refs.emailEditor.editor.exportHtml(
                    async ({ html, design }) => {
                        payload.html = html;
                        payload.design = design;
                        const { data } = await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(), payload);
                        this.$router.push({
                            path: `/app/saved-replies/${data.id}`,
                        });
                        this.template = data;
                        this.loadDesign();
                        this.$bvModal.hide('create-template-modal');
                        this.successToast({ text: 'Template created successfully' });
                        this.fetchTemplatesList();
                    },
                    {
                        minify: true,
                        inlineStyles: true,
                    }
                );
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not create template' });
            } finally {
                this.busy = false;
            }
        },
        async onDelete() {
            try {
                this.busy = true;
                await api.delete(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId));

                const templates = Object.values(this.systemTemplates).flatMap((group) =>
                    Object.values(group).flatMap((type) => type)
                );

                const nearestTemplate = templates.find(
                    (template) =>
                        template.template_type === TEMPLATE_TYPES.USER.value && template.id !== this.templateId
                );

                this.$router.push({
                    path: `/app/saved-replies/${nearestTemplate?.id || 0}`,
                });

                if (nearestTemplate) {
                    this.template = nearestTemplate;
                } else {
                    this.template = {
                        id: 0,
                        name: '',
                        template_type: TEMPLATE_TYPES.USER.value,
                        template_group: TEMPLATE_GROUPS.CUSTOM,
                        design: {},
                        html: '',
                    };
                }

                await this.fetchTemplatesList();
                this.loadDesign();
                this.successToast({ text: 'Template deleted successfully' });
            } catch (e) {
                console.log(e);
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not delete template' });
            } finally {
                this.busy = false;
            }
        },
        async save({ callback = null, values = {}, showBusy = true, refetch = true }) {
            const payload = {
                ...this.template,
                ...values,
                design: {},
                html: '',
            };

            if (values.is_default_rental_agreement) {
                await this.setAsDefaultRentalAgreement();
            }

            this.$refs.emailEditor.editor.exportHtml(
                async (data) => {
                    payload.html = data.html;
                    payload.design = data.design;
                    try {
                        if (showBusy) {
                            this.busy = true;
                        }
                        const endpoint = UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId);
                        const { data: responseData } = await api.put(endpoint, payload);
                        this.template = responseData;
                        if (refetch) {
                            this.fetchTemplatesList();
                        }
                        if (callback) callback();
                    } catch (e) {
                        const { generic } = formatResponseError(e);
                        this.errorToast({ text: generic || 'Could not save template' });
                    } finally {
                        if (showBusy) {
                            this.busy = false;
                        }
                    }
                },
                {
                    minify: true,
                    inlineStyles: true,
                }
            );
        },
        setEmailData() {
            this.$refs.send_test_email_form.setValues({
                email: this.userEmail,
            });
        },
        async sendTestEmail(values) {
            try {
                this.busy = true;
                this.save({
                    callback: async () => {
                        await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId) + 'send-test-email/', {
                            email: values.email,
                        });
                        this.successToast({ text: 'Test email sent successfully' });
                    },
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not send test email' });
            } finally {
                this.busy = false;
            }
        },
        validateTemplate() {
            this.template.require_validation = false;
            this.save({
                callback: () => {
                    this.successToast({ text: 'Template validated successfully' });
                },
            });
        },
        async saveGlobalSettings(settings) {
            try {
                this.busy = true;
                await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'global-settings/', settings);
                this.successToast({ text: 'Global settings saved successfully' });

                await this.fetchTemplatesList();

                const templates = Object.values(this.systemTemplates).flatMap((group) => Object.values(group).flat());

                this.template = templates.find((template) => template.id === this.templateId);
                this.loadDesign();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save global settings' });
            } finally {
                this.busy = false;
            }
        },
        extractText() {
            try {
                this.$refs.emailEditor.editor.exportPlainText(async (data) => {
                    await navigator.clipboard.writeText(data.text);
                    this.successToast({ text: 'Text copied to clipboard successfully' });
                });
            } catch (err) {
                this.errorToast({ text: 'Something went wrong while copying to clipboard.' });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.editor-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
}
.editor-container {
    border-top: 1px solid #e0e0e0;
    position: relative;
}
:deep(.template-name-input input.ldx-form-input) {
    outline: none;
    background: #f1f1f1;
    border-radius: 5px;
}

:deep(.template-editor-body .ag-body-horizontal-scroll) {
    min-height: 15px;
    height: 15px;
}

.text-icon-container {
    position: absolute;
    top: 0;
    left: 98px;
    background-color: rgb(244, 244, 244);
    padding: 0 1.15em;
    border-right: 1px solid #e0e0e0;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
